import { useEffect, useState } from "react";
import { FaEye, FaStop, FaInfoCircle, FaDownload } from "react-icons/fa";
import useAuth from "../../../../Hooks/useAuth";
import useMaintenanceSocket from "../../../../Hooks/device/useMaintenanceSocket";
import useHangerSensorLive from "../../../../Hooks/device/useHangerSensorLive";
import useDeviceStatusLive from "../../../../Hooks/device/useDeviceStatusLive";
import { EmptyState } from "../../../shared";
import OperationStatusPanel from "./OperationStatusPanel";
import CupGrinderSensorSection from "./CupGrinderSensorSection";
import { useAlert } from "../../../../context/AlertContext";
import {
  SectionCard,
  LiveBadge,
  Notice,
  ValueRowsSkeleton,
  MaintenanceSkeleton,
  Spinner,
  BTN_PRIMARY,
  BTN_SECONDARY,
  BTN_DANGER,
  SegmentedTabs,
} from "./MaintenanceUi";
import {
  HANGER_HISTORY_LIMIT,
  QUALITY_STYLE,
  QUALITY_TEXT,
  downloadHangerHistory,
  expectedText,
  stateText,
  valueQuality,
} from "./hangerSensorHistory";
import HangerSensorExplainer from "./HangerSensorExplainer";
import { isOkcDiffMode } from "./hangerSensitivity";
import {
  deviceHasGrinder,
  getDeviceCupNumber,
} from "../../../../utils/deviceUtils";
import { isOkcDevice } from "../okcSettingsTab/okcSettingsModel";
import { useWriteOkcSettingsMutation } from "../../../../store/storageRoles/devicesSettingsV2Api";

// Sozlesme: BayinerIOT-Workspace- docs/api-contracts/hanger-sensor-live.md (#42)
// Tabletteki "Askı Sensörü" cekmecesinin uzaktan karsiligi. Uzaktan askı
// hareket komutu BILEREK yok; yalniz izleme + goz basina hassasiyet.
// Sensör satırı (#132): geniş ekranda yan yana [Askı] [Fincan] [Değirmen]
// kutuları (değirmen yoksa iki kutu), dar ekranda alt alta.

// Sensör kutuları: dar ekranda tam genişlik, xl'de eşit sütun.
const SENSOR_BOX = "min-w-0 xl:flex-1 xl:basis-0";

const DEFAULT_SENS = 5;

const REASON_TEXT = {
  forbidden: "Bu cihazı izleme yetkiniz yok.",
  not_found: "Cihaz bulunamadı.",
  invalid_serial: "Cihazın seri numarası geçersiz.",
  too_many: "Aynı anda en fazla 3 cihaz izlenebilir.",
  error: "Sunucu izlemeyi başlatamadı. Sayfayı yenileyip tekrar deneyin.",
};

const fmt = (v) => (v === null || v === undefined ? "—" : String(v));

function ValueRow({ label, value, hint, quality }) {
  return (
    <div
      className={`flex items-start justify-between gap-3 rounded-lg border px-3 py-2 ${
        quality
          ? QUALITY_STYLE[quality]
          : "bg-white border-gray-200 text-gray-800"
      }`}
    >
      <div className="min-w-0">
        <div className="text-sm font-SemiBold">{label}</div>
        {hint && <div className="text-xs opacity-80 mt-0.5">{hint}</div>}
      </div>
      <div className="text-lg font-Bold tabular-nums shrink-0">{value}</div>
    </div>
  );
}

// Uzaktan askı izleme (v1.1): göz seç + başlat/durdur. Makinede çekmece
// açıksa ölçülen göz makinenin seçtiğidir (öncelik makinede) — kullanıcıya
// söylenir.
function RemoteControlBar({
  cupCount,
  remoteCup,
  liveCup,
  liveSource,
  disabled,
  error,
  onStart,
  onStop,
}) {
  const [cup, setCup] = useState(remoteCup || 1);
  // Başlat/göz değişimi gönderildi, server onayı bekleniyor (buton döner).
  const [pending, setPending] = useState(null);
  useEffect(() => {
    if (remoteCup) setCup(remoteCup);
    setPending(null);
  }, [remoteCup]);
  useEffect(() => {
    if (error) setPending(null);
  }, [error]);
  const running = !!remoteCup;
  const overridden =
    liveSource === "drawer" && liveCup && liveCup !== remoteCup;
  const start = (n) => {
    setPending(n);
    onStart(n);
  };
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-xs font-SemiBold tracking-wide text-gray-500">
          GÖZ
        </span>
        <SegmentedTabs
          options={Array.from({ length: cupCount }, (_, i) => ({
            id: i + 1,
            label: String(i + 1),
          }))}
          value={cup}
          disabled={disabled}
          onChange={(n) => {
            setCup(n);
            if (running) start(n);
          }}
        />
        {running ? (
          <button
            type="button"
            onClick={onStop}
            className={`ml-auto ${BTN_DANGER}`}
          >
            <FaStop className="w-3 h-3" />
            İzlemeyi durdur
          </button>
        ) : (
          <button
            type="button"
            onClick={() => start(cup)}
            disabled={disabled || pending != null}
            className={`ml-auto ${BTN_PRIMARY}`}
          >
            {pending != null ? <Spinner /> : <FaEye className="w-3.5 h-3.5" />}
            Askı izlemeyi başlat
          </button>
        )}
      </div>
      {overridden && (
        <Notice
          icon={FaInfoCircle}
          tone="amber"
          title={`Makinede Askı Sensörü ekranı açık — Göz ${liveCup} ölçülüyor`}
        >
          Makinedeki seçim öncelikli; ekran kapanınca Göz {remoteCup}'e dönülür.
        </Notice>
      )}
      {error && (
        <Notice icon={FaInfoCircle} tone="rose" title="İzleme başlatılamadı">
          {REASON_TEXT[error] || error}
        </Notice>
      )}
    </div>
  );
}

/**
 * Bakım sekmesi — canlı operasyon durumu (mobil uygulamadaki gibi) +
 * askı sensörü canlı okuma ve uzaktan hassasiyet (yalnız Fincanda).
 * @param {object} props
 * @param {object} props.selectedDevice
 * @param {Function} props.useWriteSettings rol grubunun v2 yazma mutation
 *   hook'u (depo: useWriteDeviceSettingsV2Mutation, bayi:
 *   useWriteDealerDeviceSettingsV2Mutation)
 */
export default function MaintenanceTab({ selectedDevice, useWriteSettings }) {
  const { auth } = useAuth();
  const token = auth?.accessToken;
  const serialNo = selectedDevice?.serialNo;
  const deviceName =
    selectedDevice?.deviceInfo?.deviceName || selectedDevice?.deviceName || "";
  const hasTurkishCoffee = deviceName.includes("Fincanda");
  const hasTeaFilter =
    deviceName.includes("Çayset") || deviceName.includes("Çay-Filtre");
  const online = selectedDevice?.deviceInfo?.connection?.status;
  // Eski kart seti (OKC, #365): canlı izleme var, hassasiyet ayarı ve
  // değirmen kartı yok — v2 yazma ucu OKC'ye 400 döner (okc-live-status.md 7).
  const isOkc = isOkcDevice(selectedDevice);
  const { socket, ready, error: socketError } = useMaintenanceSocket(token);
  const operation = useDeviceStatusLive(socket, ready, serialNo);
  const {
    status,
    error,
    live,
    closedReason,
    remoteCup,
    remoteError,
    history,
    start: startRemote,
    stop: stopRemote,
  } = useHangerSensorLive(
    socket,
    hasTurkishCoffee ? ready : false,
    hasTurkishCoffee ? serialNo : null,
  );
  const [writeMutation] = useWriteSettings();
  // Eski kart seti (WS#398): hassasiyetler 4G'de, OKC ayar ucuyla yazılır
  // (göz 0 tabanlı). v2 anahtarı OKC anahtarına çevrilir.
  const [writeOkcMutation] = useWriteOkcSettingsMutation();
  const OKC_SENS_KEY = {
    hangerSensitivity: "hangerSensitivity",
    cupSensorSensitivity: "cupSensitivity",
  };
  const { showSuccess, showError } = useAlert();

  // Tek ayar yazımı (v2 settings/WRITE) — askı, fincan ve değirmen
  // hassasiyeti ortak kullanır. Makine reddederse sonucu gösterir.
  const writeSetting = async (change, label) => {
    try {
      const okcKey = isOkc ? OKC_SENS_KEY[change.key] : null;
      if (isOkc && !okcKey) {
        showError(`${label}: bu ayar eski kart setinde yok.`);
        return false;
      }
      const resp = okcKey
        ? await writeOkcMutation({
            id: selectedDevice?._id,
            token,
            changes: [{ key: okcKey, cup: change.potIndex - 1, value: change.value }],
          }).unwrap()
        : await writeMutation({
            id: selectedDevice?._id,
            token,
            changes: [change],
          }).unwrap();
      const payload = resp?.data ?? resp;
      const result = Array.isArray(payload?.results)
        ? payload.results[0]
        : null;
      if (result && result.success === false) {
        showError(
          `${label}: makine değeri kabul etmedi (${result.reason || "bilinmeyen neden"}).`,
        );
        return false;
      }
      showSuccess(
        `${label} hassasiyeti ${change.value} olarak gönderildi; makineden gelen bir sonraki okumada görünür.`,
      );
      return true;
    } catch (e) {
      showError(
        e?.data?.message ||
          "Ayar gönderilemedi. Makinenin çevrimiçi olduğundan emin olup tekrar deneyin.",
      );
      return false;
    }
  };

  // Askı hassasiyeti (açıklama panelindeki 1-10 kutularından).
  const handleSave = (cup, value) =>
    writeSetting(
      {
        group: "custom",
        potIndex: cup,
        type: "TC",
        subgroup: "turkishCoffee",
        key: "hangerSensitivity",
        value,
      },
      `Göz ${cup}`,
    );

  if (!serialNo) {
    return <EmptyState message="Cihazın seri numarası yok" />;
  }

  const watchError =
    socketError ||
    (operation.status === "error" ? operation.error : null) ||
    (status === "error" ? error : null);
  if (watchError) {
    return (
      <EmptyState
        message="Canlı izleme başlatılamadı"
        suggestion={REASON_TEXT[watchError] || REASON_TEXT.error}
      />
    );
  }

  const quality = live ? valueQuality(live) : null;
  // Göz sayısı: canlı durumdan (tablet gönderir), yoksa cihaz kaydından.
  const tcCupCount = Math.min(
    6,
    Math.max(
      1,
      (Array.isArray(operation.data?.cups) && operation.data.cups.length) ||
        (Array.isArray(live?.sensitivities) && live.sensitivities.length) ||
        getDeviceCupNumber(selectedDevice) ||
        2,
    ),
  );
  const sensitivities = Array.isArray(live?.sensitivities)
    ? live.sensitivities
    : [];

  // İlk bağlantı — iskelet üstünde maskot (site yükleme kuralı).
  if (!ready) {
    return (
      <div className="h-full overflow-y-auto bg-gray-50/40 px-4 sm:px-6 py-4">
        <MaintenanceSkeleton
          message="Makineye bağlanılıyor..."
          cardCount={hasTurkishCoffee ? 4 : 3}
          withHanger={hasTurkishCoffee}
        />
      </div>
    );
  }

  const hangerBadge = live ? "live" : remoteCup ? "waiting" : "off";

  return (
    // Tam genişlik: web görünümde içerik boşluğa yayılır (ortada dar sütun yok).
    <div className="h-full overflow-y-auto bg-gray-50/40 px-4 sm:px-6 py-4">
      <div className="w-full flex flex-col gap-4">
        <OperationStatusPanel
          live={operation}
          online={online}
          hasTurkishCoffee={hasTurkishCoffee}
          hasTeaFilter={hasTeaFilter}
        />
        {hasTurkishCoffee && (
          <div
            data-testid="sensor-row"
            className="flex flex-col gap-4 xl:flex-row xl:items-start"
          >
            <SectionCard
              testId="hanger-sensor-box"
              className={SENSOR_BOX}
              icon={FaEye}
              iconBg="bg-amber-500"
              title="ASKI SENSÖRÜ"
              right={
                <>
                  {history.length > 0 && (
                    <button
                      type="button"
                      onClick={() => downloadHangerHistory(history, serialNo)}
                      title={`Son ${HANGER_HISTORY_LIMIT} okumaya kadar (durum, seviye, sapan/düz örnek, göz durumu) CSV olarak indirilir`}
                      className={BTN_SECONDARY}
                    >
                      <FaDownload className="w-3.5 h-3.5" />
                      Son {history.length} değeri indir
                    </button>
                  )}
                  <LiveBadge state={hangerBadge} />
                </>
              }
            >
              <RemoteControlBar
                cupCount={tcCupCount}
                remoteCup={remoteCup}
                liveCup={live?.cup ?? null}
                liveSource={live?.source ?? null}
                disabled={status !== "watching"}
                error={remoteError}
                onStart={startRemote}
                onStop={stopRemote}
              />

              {!live && remoteCup ? (
                // Makineden ilk değer beklenirken aktivite: iskelet satırlar
                <div className="flex flex-col gap-2">
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <Spinner className="w-3 h-3" />
                    Göz {remoteCup} ölçülüyor… Birkaç saniye içinde gelmezse
                    makinede Operasyon Modu kapalı olabilir ya da makine
                    çevrimdışıdır.
                  </div>
                  <ValueRowsSkeleton rows={4} />
                </div>
              ) : !live ? (
                <Notice
                  icon={FaInfoCircle}
                  tone="gray"
                  title="Askı sensörü izlenmiyor"
                >
                  {closedReason === "session_limit"
                    ? "Makinedeki ekran 10 dakika açık kaldığı için yayın durdu. Yukarıdan yeniden başlatabilirsiniz."
                    : "Göz seçip Askı izlemeyi başlat'a basın. Makinede Operasyon Modu açık olmalı."}
                </Notice>
              ) : (
                <section className="flex flex-col gap-2">
                  <h3 className="text-sm font-SemiBold text-gray-600">
                    Canlı değerler · Göz {live.cup}
                  </h3>
                  <ValueRow
                    label="Durum"
                    value={stateText(live.state)}
                    hint={expectedText(live.brewing)}
                  />
                  {!isOkc && (
                    // Eski kart setinde (OKC) ham seviye yok (WS#398); sapan /
                    // düz örnek yeni askı kartının yöntemiyle gelir.
                  <ValueRow
                    label="Ortalama seviye"
                    value={fmt(live.level)}
                    hint="Ham ADC (0-4095). Sabit mi, oynuyor mu, uca mı yapışık diye okunur."
                  />
                  )}
                  {isOkcDiffMode(live) ? (
                    // Eski kart askısı fark yöntemiyle ölçer (askı v0.4.6+).
                    <ValueRow
                      label="Fark"
                      value={fmt(live.diff)}
                      hint={`0-255, LED açık − kapalı farkı. Yerinde kararı için ${live.diffThreshold ?? 13}'ün altında olmalı.`}
                    />
                  ) : (
                    <>
                  <ValueRow
                    label="Sapan örnek"
                    value={fmt(live.dev)}
                    hint="0-255. Yerinde kararı için 7'nin altında olmalı."
                    quality={quality}
                  />
                  <ValueRow
                    label="Düz örnek"
                    value={fmt(live.flat)}
                    hint="0-255. Yerinde kararı için 243'ün üstünde olmalı."
                    quality={quality}
                  />
                    </>
                  )}
                  {quality && !isOkcDiffMode(live) && (
                    <p className="text-xs text-gray-500">
                      {QUALITY_TEXT[quality]}
                    </p>
                  )}
                  <p className="text-xs text-gray-400">
                    Verinin yaşı:{" "}
                    {live.ageMs === null
                      ? "karttan hiç veri gelmedi"
                      : `${live.ageMs} ms`}
                  </p>
                  <ValueRow
                    label="Hassasiyet"
                    value={fmt(sensitivities[live.cup - 1] ?? live.sensitivity)}
                    hint={`1-10, fabrika ${DEFAULT_SENS}. Aşağıdaki tablodan değiştirilir.`}
                  />
                </section>
              )}
              <HangerSensorExplainer
                key={live?.cup ?? remoteCup ?? "none"}
                live={live}
                history={history}
                onSaveSensitivity={
                  live?.cup ? (v) => handleSave(live.cup, v) : undefined
                }
              />
            </SectionCard>
            <CupGrinderSensorSection
              socket={socket}
              ready={ready}
              serialNo={serialNo}
              cupCount={tcCupCount}
              onWriteSetting={writeSetting}
              hasGrinder={!isOkc && deviceHasGrinder(selectedDevice)}
              boxClassName={SENSOR_BOX}
            />
          </div>
        )}
      </div>
    </div>
  );
}
